<template>
    <div class="rounded-lgr border-valence-grey-250 border bg-white px-2 py-2 w-full">
        <div class="flex justify-between items-center px-2 py-2 overflow-clip whitespace-nowrap">{{ title }}</div>
        <div class="relative w-full h-[calc(100%-36px)]">
            <div class="absolute top-0 left-0 w-full h-full">
                <canvas ref="canvas"></canvas>
            </div>
            <div v-if="isLoading" class="absolute top-0 left-0 w-full h-full p-2">
                <SkeletonLoader class="w-full h-full rounded-lgr" />
            </div>
            <div v-if="hasNoData" class="absolute top-0 left-0 w-full h-full p-2 flex items-center justify-center text-valence-grey-400">No data available.</div>
        </div>
    </div>
</template>

<script setup lang="ts">
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import { Chart, type ChartDataset, LinearScale, type ScriptableContext, Tooltip } from "chart.js";
import { TreemapController, TreemapElement } from "chartjs-chart-treemap";
import { computed, type MaybeRef, onMounted, onUnmounted, ref, useTemplateRef, watch } from "vue";

export interface TreeMapDataItem {
    label: string;
    value: number;
    backgroundColor?: string;
    /** This will be passed back to the parent as-is in the 'click' event */
    extraData?: unknown;
}

const {
    data = [],
    title = "",
    isLoading = false,
    formatValue = (v) => `${v}`,
    responsive = true,
} = defineProps<{
    title?: MaybeRef<string>;
    data?: TreeMapDataItem[];
    isLoading?: boolean;
    formatValue?: (value: number) => string;
    responsive?: boolean; // Used only in testing to ensure we catch missing registrations
}>();

const emit = defineEmits<{
    click: [data: unknown];
}>();

const canvasRef = useTemplateRef("canvas");
const chartRef = ref<Chart | null>(null);

const hasNoData = computed(() => {
    if (isLoading) {
        return false;
    }
    for (const item of data) {
        if (item.value) {
            return false;
        }
    }
    return true;
});

const DEFAULT_COLORS = [
    "rgb(145, 211, 225)",
    "rgb(149, 197, 135)",
    "rgb(177, 182, 253)",
    "rgb(223, 248, 182)",
    "rgb(242, 133, 186)",
    "rgb(242, 208, 255)",
    "rgb(248, 240, 182)",
    "rgb(255, 211, 168)",
];

// Helper function to extract the source data, so we only have to suppress the ts-error in one place.
const getDatasetData = (dataset: ScriptableContext<"treemap">["dataset"] | ChartDataset | undefined, index: number) => {
    // @ts-expect-error '_data' is not in the Chart.js types, but it's the only way to get the original data item.
    return dataset?.data[index]?._data as TreeMapDataItem | undefined;
};

const LABEL_FONT = {
    family: "Inter, sans-serif",
    size: 14,
    weight: 500,
};
const BOX_SPACING_PX = 7; // Space between boxes, like an outside margin
const BOX_PADDING_PX = 10; // Padding between box edges and internal text
const MIN_USABLE_WIDTH_PX = 40; // Below this width, hide the box label
const MIN_USABLE_HEIGHT_PX = 16; // Below this height, hide the box label

/**
 * Splits a string of text into multiple word-wrapped lines based on the rendering context and
 * available width.
 *
 * @param ctx The 2D canvas rendering context
 * @param text The text to be wrapped
 * @param maxWidth The maximum available width for the text
 *
 * @returns An array of strings, where each string is a complete line that fits inside the available
 * width.
 */
const wrapLines = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number) => {
    // We need to do the calculations with the label font, not whatever's currently on the context
    ctx.save();
    ctx.font = `${LABEL_FONT.weight} ${LABEL_FONT.size}px ${LABEL_FONT.family}`;

    const words = text.split(" ");
    const result = [];
    let line = "";

    for (const word of words) {
        const wordWidth = ctx.measureText(word).width;
        if (wordWidth > maxWidth) {
            if (line) {
                result.push(line);
                line = "";
            }
            result.push(word);
            continue;
        }

        const testLine = (line + " " + word).trim();
        const testWidth = ctx.measureText(testLine).width;
        if (testWidth > maxWidth) {
            result.push(line);
            line = word;
        } else {
            line = testLine;
        }
    }
    if (line) {
        result.push(line);
    }

    ctx.restore();

    return result;
};

const updateChart = (newIsLoading: boolean, newData: TreeMapDataItem[]) => {
    if (!canvasRef.value) {
        return;
    }
    if (newIsLoading) {
        newData = [];
    }

    chartRef.value?.destroy();
    chartRef.value = new Chart(canvasRef.value, {
        type: "treemap",
        data: {
            datasets: [
                {
                    // @ts-expect-error Chart.js types are out-of-date for treemap
                    tree: newData,
                    key: "value",

                    // For some reason, `backgroundColor` doesn't work under
                    // `options.datasets.treemap`, which is why it's here instead.
                    backgroundColor: (ctx) => {
                        if (ctx.type !== "data") {
                            return;
                        }
                        const data = getDatasetData(ctx.dataset, ctx.dataIndex);
                        if (data?.backgroundColor) {
                            return data.backgroundColor;
                        }
                        return DEFAULT_COLORS[ctx.dataIndex % DEFAULT_COLORS.length];
                    },
                },
            ],
        },
        options: {
            animation: false,
            responsive,
            datasets: {
                treemap: {
                    spacing: BOX_SPACING_PX,
                    // @ts-expect-error Chart.js types are out-of-date, missing borderRadius here
                    borderRadius: 5,
                    borderWidth: 0,
                    labels: {
                        display: true,
                        font: LABEL_FONT,
                        position: "top",
                        align: "left",
                        padding: BOX_PADDING_PX,
                        color: "rgba(0,0,0,0.6)",
                        formatter: (ctx) => {
                            if (ctx.type != "data") {
                                return;
                            }
                            const data = getDatasetData(ctx.dataset, ctx.dataIndex);
                            if (!data) {
                                return;
                            }

                            const usableWidth = (ctx.raw?.w ?? 0) - BOX_PADDING_PX * 2 - BOX_SPACING_PX * 2;
                            const usableHeight = (ctx.raw?.h ?? 0) - BOX_PADDING_PX * 2 - BOX_SPACING_PX * 2;
                            if (usableWidth < MIN_USABLE_WIDTH_PX || usableHeight < MIN_USABLE_HEIGHT_PX) {
                                // No room for any meaningful text
                                return "";
                            }

                            const canvasContext = ctx.chart.ctx;
                            return [...wrapLines(canvasContext, data.label, usableWidth), formatValue(data.value)];
                        },
                    },
                },
            },
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        title: (ctx) => {
                            if (ctx.length === 0) {
                                return;
                            }
                            const data = getDatasetData(ctx[0]?.dataset, ctx[0]?.dataIndex || 0);
                            return data ? data.label : "";
                        },
                        label: (ctx) => {
                            const data = getDatasetData(ctx.dataset, ctx.dataIndex);
                            return data ? formatValue(data.value) : "";
                        },
                    },
                },
                datalabels: {
                    display: false,
                },
            },
            onClick: (evt, elements, chart) => {
                if (elements.length === 0) {
                    return;
                }
                const element = elements[elements.length - 1];
                if (!element) {
                    return;
                }
                const dataset = chart.data.datasets[element.datasetIndex];
                const clickedData = getDatasetData(dataset, element.index);
                if (!clickedData) {
                    return;
                }
                emit("click", clickedData.extraData);
            },
        },
    });
};

watch([() => isLoading, () => data], ([newIsLoading, newData]) => updateChart(newIsLoading, newData));

onMounted(() => {
    // Chart.js is smart enough to only register these globally once.
    Chart.register(TreemapController, TreemapElement, Tooltip, LinearScale);
    updateChart(isLoading, data);
});

onUnmounted(() => {
    chartRef.value?.destroy();
});
</script>
